import React, { useState, useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from 'react-i18next';
import { isDesktop } from '../utils/breakpoints';

gsap.registerPlugin(ScrollTrigger);

const easeOutExpo = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

const STATS = [
  {
    icon: (
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    value: 20, suffix: '+', display: null, labelKey: 'stats.yearsExperience',
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-5.478-3.764M9 20H4v-2a4 4 0 015.478-3.764M15 11a4 4 0 11-8 0 4 4 0 018 0zm6 0a3 3 0 11-6 0 3 3 0 016 0zM3 11a3 3 0 116 0 3 3 0 01-6 0z" />
      </svg>
    ),
    value: 290000, suffix: '', display: null, labelKey: 'stats.guestsAnnually',
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
      </svg>
    ),
    value: 8, suffix: '+', display: null, labelKey: 'stats.languages',
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    value: null, suffix: '', display: 'Global', labelKey: 'stats.partners',
  },
  {
    icon: (
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    value: null, suffix: '', display: '✦', labelKey: 'stats.excellence',
  },
];

const Hero = () => {
  const { t } = useTranslation('hero');

  const stats = STATS;


  const containerRef = useRef(null);
  const textRef      = useRef(null);
  const overlayRef   = useRef(null);
  const videoRef     = useRef(null);
  const statsBarRef  = useRef(null);
  const rafRef       = useRef(null);
  const mobileOverlayRef = useRef(null);

  const [isMuted, setIsMuted]     = useState(true);
  const [counts, setCounts]       = useState(stats.map(s => (s.value !== null ? 0 : null)));
  const [statIndex, setStatIndex] = useState(0);
  const [fadeIn, setFadeIn]       = useState(true);

  const toggleAudio = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  useEffect(() => {
    const duration  = 2200;
    const startTime = performance.now() + 400;

    const tick = (now) => {
      if (now < startTime) { rafRef.current = requestAnimationFrame(tick); return; }
      const elapsed  = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const ease     = easeOutExpo(progress);

      setCounts(stats.map(s => (s.value !== null ? Math.round(ease * s.value) : null)));

      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.set('.stat-item', { opacity: 0, y: 24 });
      gsap.to('.stat-item', {
        opacity: 1, y: 0, duration: 0.7, stagger: 0.12, ease: 'power3.out', delay: 0.3,
      });

      const isMobile = !isDesktop(); // tablets μετράνε ως mobile

      // Mobile: ΧΩΡΙΣ pin -- το Hero scrollάρει φυσικά μαζί με τη σελίδα,
      // χωρίς καμία "κολλημένη"/θολή ενδιάμεση φάση. Το ήδη υπάρχον smooth
      // scrolling του Lenis δίνει από μόνο του premium αίσθηση στη μετάβαση.
      if (isMobile) {
        // Χωρίς scroll-linked animation -- καθαρό, άμεσο πέρασμα στο επόμενο section.
      } else {
        const tl = gsap.timeline({
          scrollTrigger: {
            id: 'hero-desktop',
            trigger: containerRef.current,
            start: 'top top', end: 'bottom top',
            scrub: true, pin: true, pinSpacing: true, anticipatePin: 1,
          },
        });
        tl.to('.hero-subtitle', { y: -150, opacity: 0, duration: 1 }, 0);
        tl.to('.hero-title',    { y: -150, opacity: 0, scale: 0.95, duration: 1 }, 0);
        tl.to('.hero-stats',    { y: -100, opacity: 0, duration: 1 }, 0);
        tl.to(overlayRef.current, { backgroundColor: 'rgba(0,0,0,0.7)', duration: 1 }, 0);
      }
    }, containerRef);

    return () => {
      ctx.revert();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setStatIndex(prev => (prev + 1) % stats.length);
        setFadeIn(true);
      }, 180); // πριν: 400ms -- το μεγάλο "κενό" ήταν αυτό που ένιωθες σαν καθυστέρηση
    }, 2000); // πριν: 2500ms -- πιο ζωηρός ρυθμός εναλλαγής
    return () => clearInterval(interval);
  }, []);

  const formatCount = (count, stat) => {
    if (stat.value === null) return stat.display;
    if (stat.value === 290000) return count.toLocaleString('en-US');
    return String(count) + stat.suffix;
  };

  return (
    <div ref={containerRef} className="relative h-screen w-full overflow-hidden">

      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          fetchpriority="high"
          poster="/hero-poster.jpg"
          className="h-full w-full object-cover"
        >
          <source src="/bgmusic-mobile.mp4" type="video/mp4" media="(max-width: 768px)" />
          <source src="/bgmusic.mp4" type="video/mp4" />
        </video>
      </div>

      <div ref={overlayRef} className="absolute inset-0 z-10 bg-black/30" />
      <div
        ref={mobileOverlayRef}
        className="md:hidden absolute inset-0 z-20"
        style={{ backgroundColor: 'rgba(249,245,233,0)', transition: 'none', pointerEvents: 'none' }}
      />

      <button
        onClick={toggleAudio}
        className="absolute bottom-8 right-8 z-[100] flex items-center justify-center w-12 h-12 rounded-full border border-white/30 bg-black/20 backdrop-blur-sm text-white hover:bg-white/20 transition-all duration-300 cursor-pointer"
        aria-label={isMuted ? 'Unmute video' : 'Mute video'}
      >
        {isMuted ? (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 9.75L19.5 12m0 0l2.25 2.25M19.5 12l2.25-2.25M19.5 12l-2.25 2.25m-10.5-6l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
          </svg>
        )}
      </button>

      {/* ── DESKTOP ── */}
      <div className="hidden md:flex relative h-full flex-col items-center justify-end text-center px-4 pb-12" style={{ zIndex: 30 }}>

        <div
          className="hero-subtitle absolute top-1/2 -translate-y-1/2 left-8 text-white text-left max-w-lg"
          style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: '400', lineHeight: '1.7' }}
        >
          {t('subtitle')}
        </div>

        <div className="hero-stats w-full max-w-5xl mx-auto mb-10" ref={statsBarRef}>
          <div
            className="grid grid-cols-5"
            style={{ border: '1px solid rgba(190,149,87,0.4)', backgroundColor: 'rgba(7,29,61,0.82)', backdropFilter: 'blur(12px)' }}
          >
            {stats.map((stat, i) => (
              <div key={i} className="stat-item flex flex-col items-center justify-center gap-2 py-5 px-4"
                style={{ borderRight: i < 4 ? '1px solid rgba(190,149,87,0.2)' : 'none' }}>
                <div style={{ color: '#BE9557' }}>{stat.icon}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(18px, 1.6vw, 26px)', fontWeight: '400', color: 'white', lineHeight: '1', letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCount(counts[i], stat)}
                </div>
                <div style={{ fontFamily: 'var(--font-sans)', fontSize: '9px', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(190,149,87,0.8)', lineHeight: '1.3' }}>
                  {t(stat.labelKey)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div ref={textRef} className="will-change-transform w-full">
          <h1
            className="hero-title text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-normal text-white tracking-[0.15em] uppercase"
            style={{ fontFamily: 'Oswald, Arial, sans-serif' }}
          >
            {t('title')}
          </h1>
        </div>

      </div>

      {/* ── MOBILE ── */}
      <div className="md:hidden relative h-full flex flex-col justify-center px-0" style={{ zIndex: 30, paddingTop: '100px', paddingBottom: '24px' }}>

        <div className="flex flex-col items-center gap-6 px-4">

          <div
            className="mobile-hero-subtitle text-white"
            style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: '500', lineHeight: '1.7', fontStyle: 'italic', textAlign: 'center', width: '85%' }}
          >
            {t('subtitle')}
          </div>

          <div className="mobile-hero-ticker" style={{ width: '100%' }}>
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.25)', borderBottom: '1px solid rgba(255,255,255,0.25)', padding: '8px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ color: 'white', fontFamily: 'var(--font-sans)', fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: '500', opacity: fadeIn ? 1 : 0, transform: fadeIn ? 'translateY(0)' : 'translateY(4px)', transition: 'opacity 0.22s ease, transform 0.22s ease', whiteSpace: 'nowrap' }}>
                {formatCount(counts[statIndex], stats[statIndex])}
                <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: '400', marginLeft: '8px', fontSize: '10px', letterSpacing: '0.15em' }}>
                  {t(stats[statIndex].labelKey)}
                </span>
              </div>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5" style={{ opacity: fadeIn ? 1 : 0, transition: 'opacity 0.22s ease', marginLeft: 'auto', flexShrink: 0 }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          <div className="mobile-hero-title" style={{ width: '100%', marginTop: '24px' }}>
            <h1
              className="font-normal text-white uppercase leading-none"
              style={{ fontFamily: 'Oswald, Arial, sans-serif', fontSize: 'clamp(22px, 6.5vw, 38px)', letterSpacing: '0.03em', lineHeight: '1.05' }}
            >
              {t('titleMobile1')}<br />{t('titleMobile2')}
            </h1>
          </div>

        </div>

      </div>

    </div>
  );
};

export default Hero;